import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { fold, templateName, type PickerOption } from "./EventChannelTemplates";

type Option = {
  code: string;
  name: string | null;
  text: string;
  disabled: boolean;
};
type Group = { label: string; options: Option[] };

/**
 * The template picker shared by the clinic-wide row and the per-service matrix (#19565, #19579).
 *
 * <p>One field: type to filter, the list opens under it. It replaced a search box stacked on a
 * native select — thirteen of each on the page, and a select too narrow to show a template code.
 *
 * <p>Three groups, as before: suitable first, then those needing fixed values, and the unsuitable
 * ones only behind "Hiện n mẫu không phù hợp" — reachable, so "why is my template not offered"
 * has an answer, but not in the way. The stored template is always listed whatever its group, so
 * a binding in place never reads as "— Chưa chọn —".
 */
export function TemplateSelect({
  picks,
  value,
  onChange,
  disabled,
  emptyLabel,
  compact = false,
  invalid = false,
  channel,
  showPreviewLink = true,
}: {
  /** The channel being picked for — whose provider name to show (#19571). */
  channel?: string;
  picks: PickerOption[];
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  emptyLabel: string;
  compact?: boolean;
  invalid?: boolean;
  /** #19587 — off where the card embeds the preview and links to it itself. */
  showPreviewLink?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showRefused, setShowRefused] = useState(false);
  // -1 = nothing highlighted. Opening never pre-selects: with the empty "— Chưa chọn —" first in
  // the list, an Enter right after tabbing in would otherwise clear a saved template (review of
  // #19579). Enter acts only on what the user highlighted with an arrow, a hover, or by typing.
  const [active, setActive] = useState(-1);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const q = fold(query.trim());
  const refusedAll = useMemo(
    () => picks.filter((p) => p.problem !== null),
    [picks],
  );
  /** The empty choice, then three labelled groups — what a screen reader announces as groups. */
  const { empty, groups } = useMemo(() => {
    const nameOf = (p: PickerOption) => templateName(p.template, channel);
    const matches = (p: PickerOption) =>
      !q ||
      fold(p.template.code).includes(q) ||
      fold(nameOf(p) ?? "").includes(q);
    const opt = (
      p: PickerOption,
      note: string | null,
      off: boolean,
    ): Option => ({
      code: p.template.code,
      name: nameOf(p),
      text: note ? `${p.template.code} · ${note}` : p.template.code,
      disabled: off,
    });
    const gs: Group[] = [
      {
        label: "✅ Phù hợp",
        options: picks
          .filter(
            (p) =>
              p.problem === null && p.needsFixed.length === 0 && matches(p),
          )
          .map((p) => opt(p, null, false)),
      },
      {
        label: "✏️ Cần nhập thêm giá trị cố định",
        options: picks
          .filter(
            (p) => p.problem === null && p.needsFixed.length > 0 && matches(p),
          )
          .map((p) => opt(p, `nhập ${p.needsFixed.length} giá trị`, false)),
      },
      {
        label: "⛔ Không phù hợp",
        options: refusedAll
          .filter(
            (p) => p.template.code === value || (showRefused && matches(p)),
          )
          .map((p) => opt(p, p.problem, true)),
      },
    ].filter((g) => g.options.length > 0);
    return {
      empty: q
        ? null
        : ({
            code: "",
            name: null,
            text: emptyLabel,
            disabled: false,
          } as Option),
      groups: gs,
    };
  }, [picks, q, showRefused, value, emptyLabel, refusedAll, channel]);
  const options = [
    ...(empty ? [empty] : []),
    ...groups.flatMap((g) => g.options),
  ];
  const choosable = options.filter((o) => !o.disabled);

  const current = picks.find((p) => p.template.code === value);
  const currentName = current ? templateName(current.template, channel) : null;
  const preview = current?.template.renditions.find(
    (r) => r.previewImageUrl,
  )?.previewImageUrl;

  const openList = () => {
    if (open) return;
    setActive(-1);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setQuery("");
    setActive(-1);
  };
  // A save in flight disables the field; a list left open would still take clicks.
  useEffect(() => {
    if (disabled) close();
  }, [disabled]);
  const choose = (code: string) => {
    if (disabled) return;
    close();
    if (code !== value) onChange(code);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        // The first press only opens the list and lands on the current choice (or the top).
        setOpen(true);
        const cur = choosable.findIndex((o) => o.code === value);
        setActive(cur >= 0 ? cur : 0);
        return;
      }
      if (choosable.length === 0) return;
      setActive((i) =>
        i < 0
          ? 0
          : (i + (e.key === "ArrowDown" ? 1 : -1) + choosable.length) %
            choosable.length,
      );
    } else if (e.key === "Enter") {
      if (!open) return;
      e.preventDefault();
      const hit = active >= 0 ? choosable[active] : undefined;
      if (hit) choose(hit.code);
    } else if (e.key === "Escape") {
      close();
    }
  };
  const activeCode = open && active >= 0 ? choosable[active]?.code : undefined;
  const renderOption = (e: Option) => (
    <li
      key={e.code || "__empty__"}
      id={optionId(e.code)}
      role="option"
      aria-selected={e.code === value}
      aria-disabled={e.disabled || undefined}
      onClick={() => !e.disabled && choose(e.code)}
      onMouseEnter={() => {
        if (e.disabled) return;
        const i = choosable.findIndex((o) => o.code === e.code);
        if (i >= 0) setActive(i);
      }}
      className={
        "rounded px-2 py-1.5 text-xs " +
        (e.disabled
          ? "cursor-not-allowed text-muted-foreground"
          : "cursor-pointer " +
            (e.code === activeCode ? "bg-muted" : "hover:bg-muted")) +
        (e.code === value ? " font-medium" : "")
      }
    >
      {e.name ? (
        <>
          <span className="block">{e.name}</span>
          <span className="block break-all font-mono text-muted-foreground">
            {e.text}
          </span>
        </>
      ) : (
        <span className={"break-all" + (e.code ? " font-mono" : "")}>
          {e.text}
        </span>
      )}
    </li>
  );
  const optionId = (code: string) => `${listId}-o-${code || "empty"}`;

  return (
    <div className="grid min-w-0 gap-1">
      <div className="relative min-w-0">
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-invalid={invalid || undefined}
          aria-label="Chọn mẫu tin"
          aria-activedescendant={
            activeCode !== undefined ? optionId(activeCode) : undefined
          }
          disabled={disabled}
          title={
            value
              ? currentName
                ? `${currentName} — ${value}`
                : value
              : undefined
          }
          // While typing, the current choice stays visible as the placeholder.
          placeholder={
            open
              ? currentName || value || "Gõ để tìm mẫu theo tên hoặc mã…"
              : emptyLabel
          }
          value={open ? query : (currentName ?? value)}
          onChange={(e) => {
            setQuery(e.target.value);
            // Typing is a choice of intent: Enter now takes the first match.
            setActive(e.target.value.trim() ? 0 : -1);
            setOpen(true);
          }}
          onFocus={openList}
          onClick={openList}
          onBlur={close}
          onKeyDown={onKey}
          className={
            `${compact ? "h-8 text-xs" : "h-9 text-sm"} w-full min-w-0 rounded-md border bg-background px-2` +
            (value && !open && !currentName ? " font-mono" : "") +
            (invalid ? " border-destructive" : "")
          }
        />
        {open && (
          <ul
            id={listId}
            role="listbox"
            // Keep focus in the input: a press on the list must not blur it before the choice lands.
            onMouseDown={(e) => e.preventDefault()}
            className="absolute top-full z-20 mt-1 max-h-72 w-full min-w-[280px] overflow-auto rounded-md border bg-popover p-1 shadow-md"
          >
            {options.length === 0 && (
              <li className="px-2 py-1.5 text-xs text-muted-foreground">
                Không có mẫu khớp.
              </li>
            )}
            {empty && renderOption(empty)}
            {groups.map((g, gi) => (
              <li key={g.label} role="presentation">
                <ul role="group" aria-labelledby={`${listId}-g${gi}`}>
                  <li
                    id={`${listId}-g${gi}`}
                    role="presentation"
                    className="px-2 pb-0.5 pt-2 text-xs font-medium text-muted-foreground"
                  >
                    {g.label}
                  </li>
                  {g.options.map(renderOption)}
                </ul>
              </li>
            ))}
            {/* Inside the list it belongs to: next to the "view template" link it read as a link
                to the hidden templates' previews (user, 09/10/2026). */}
            {refusedAll.length > 0 && (
              <li role="presentation" className="mt-1 border-t px-2 pt-1.5">
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                  onClick={() => {
                    setShowRefused((s) => !s);
                    inputRef.current?.focus();
                  }}
                >
                  {showRefused
                    ? "Ẩn mẫu không phù hợp"
                    : `Hiện ${refusedAll.length} mẫu không phù hợp`}
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
      {showPreviewLink && preview && (
        <a
          href={preview}
          target="_blank"
          rel="noreferrer"
          className="justify-self-start text-xs text-action underline-offset-2 hover:underline"
        >
          Xem ảnh mẫu đang chọn ↗
        </a>
      )}
    </div>
  );
}
